import { FC, Suspense } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { CreditsCard } from '../CreditsCard';
import { GameId } from '../GameId';
import { MoreButton } from '../MoreButton';
import { MaxMinButton } from '../MaxMinButton';
import { PrebuyButton } from '../PrebuyButton';
import { CurrencyToggle } from '../CurrencyToggle';
import { FpeTokenButton } from '../FpeTokenButton';
import { LazyBingoMarketingModal } from './BingoMarketingModal.lazy';
import { HeaderProps } from './mobile';

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 2.25rem 0.5rem;
`;

const CreditsCardContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 85%;
    gap: 0.5rem;
`;

const ButtonsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 43.5%;
    margin-right: 1rem;
`;

const UsernameContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 17rem;
    height: 4.5rem;
    padding: 1.25rem;
    border-radius: 0.969rem;

    ${({ theme }) => `
        color: ${theme.username.color};
        background-color: ${theme.username.background};
    `};
`;

const UsernameText = styled.div`
    font-family: Signika-SemiBold;
    font-weight: 600;
    font-size: 1.75rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const HeaderDesktop: FC<HeaderProps> = ({
    currencyUnit,
    isOpen,
    isMax,
    gameId,
    cash,
    bonus,
    tokens = 0,
    total,
    currency,
    username,
    hideCurrencyToggle,
    marketingModalConfig,
    onMarketingModalToggle,
    onSettingsOpen,
    onToggleCurrency,
    onToggleScreen,
}) => {
    const { t } = useTranslation();
    const location = useLocation();
    const theme = useTheme();

    return (
        <>
            {location.pathname !== '/side' && (
                <>
                    <GameId gameId={gameId} />
                    <HeaderContainer>
                        <ButtonsContainer>
                            <MoreButton handleClick={onSettingsOpen} />
                            <MaxMinButton
                                isMax={isMax}
                                handleClick={onToggleScreen}
                            />
                            <PrebuyButton
                                handleClick={onMarketingModalToggle}
                            />
                            <UsernameContainer>
                                <UsernameText>{username}</UsernameText>
                            </UsernameContainer>
                        </ButtonsContainer>
                        <CreditsCardContainer>
                            {!hideCurrencyToggle &&
                                !theme.currencyToggle.hide && (
                                    <CurrencyToggle
                                        currency={currency}
                                        onClick={onToggleCurrency}
                                    />
                                )}

                            <FpeTokenButton token={tokens || 0} />

                            {!theme.credit.hide?.cash && (
                                <CreditsCard
                                    type="CASH"
                                    title={t('nav.' + currencyUnit)}
                                    body={cash}
                                    hideCurrencyToggle={hideCurrencyToggle}
                                />
                            )}
                            {!theme.credit.hide?.bonus && (
                                <CreditsCard
                                    type="BONUS"
                                    title={t('nav.bonus')}
                                    body={bonus}
                                    hideCurrencyToggle={hideCurrencyToggle}
                                />
                            )}
                            {!theme.credit.hide?.total && (
                                <CreditsCard
                                    type="TOTAL"
                                    title={t('nav.total')}
                                    body={total}
                                    hideCurrencyToggle={hideCurrencyToggle}
                                />
                            )}
                        </CreditsCardContainer>
                    </HeaderContainer>
                    {marketingModalConfig && (
                        <Suspense fallback={null}>
                            <LazyBingoMarketingModal
                                {...marketingModalConfig}
                                isOpen={isOpen}
                                onClose={onMarketingModalToggle}
                            />
                        </Suspense>
                    )}
                </>
            )}
        </>
    );
};
